/**
 * **联机房间页** —— 玩家列表、游戏规则、踢人、存档导入。
 *
 * 评审：「联机房间页需要可以改变房间的各类游戏规则设置，存档导入，以及踢人等
 * （仅创建房间者可以使用这些功能）」
 *
 * ## 这一屏最要紧的一件事：**两个"房主"不是同一个人**
 *
 * | 角色 | 谁 | 能干什么 |
 * | --- | --- | --- |
 * | **创建者** `isCreator` | 开房间的那个人 | 改游戏规则、踢人、导存档 |
 * | **对局房主** `electGameHost()` | **网络最快的人** | 开局后当权威 |
 *
 * 评审明确要求「进入游戏后的 host 仅由房间中网络最快者担任，和谁创建房间无关」，
 * 所以这一屏把两个身份**分开显示**：创建者挂"创建者"章，
 * 延迟最低的那个人挂"对局房主"章 —— 它们经常不是同一个人。
 * `electGameHost()` 在 `room.ts` 里是纯函数、有测试钉着（平局按 id 字典序，
 * 保证所有 peer 独立算出同一个答案）。
 *
 * ## 权限不是靠"把按钮藏起来"实现的
 *
 * 界面会把创建者才能用的控件**禁用并说明原因**，但真正的判定在
 * `room.ts` 的 `patchConfig` / `kickPlayer` 里 —— 它们不看界面，
 * 自己检查 `isSelfCreator()`。将来接上网络时，远端请求走的是同一条判定。
 * 界面上的禁用只是"提前告诉玩家为什么不能点"，不是安全边界。
 */

import { Container, Graphics } from 'pixi.js';
import {
  STAMP_GAP,
  createColumn,
  infoRow,
  richLabel,
  notice,
  pageChrome,
  panel,
  scrollArea,
  sectionTitle,
  signalBar,
  slider,
  stamp,
  stampButton,
  stepper,
  toggle,
  uiPad,
  INK,
} from '../../render/ui';
import { copyCode } from '../../render/ui/copyable';
import { createSurface, type ControlMotion, type ControlState } from '../../render/ui/state';
import { MAX_COPIES_PER_TITLE, MAX_PLAYERS, type RoomConfig } from '../../core/config';
import type { PlayerId } from '../../core/ids';
import { cardName } from '../../core/cardText';
import {
  deckTotal,
  electGameHost,
  findPlayer,
  isSelfCreator,
  packStatuses,
  roomJoinUrl,
  type ConfigPatch,
  type LobbyPlayer,
} from '../room';
import { houseRules, type HouseRule } from '../rules';
import { parseRoomPreset, serializeRoomPreset } from '../roomPreset';
import type { Screen, ScreenContext } from '../screen';
import { OVERLAY, SCREEN } from './ids';
import { bi, t } from '../../i18n';

/** 面板内边距（设计像素） */
const PANEL_PADDING = 16;
/**
 * 内容比面板 body 再窄这么多。
 *
 * ⚠️ 面板里的 `scrollArea` 是一块**矩形遮罩**，按钮正好和它同宽时右边会被裁掉
 *    一条边（用户报的"窗口裁掉按钮边缘"）。留出这段余量给遮罩，就不裁了。
 */
const CONTENT_INSET = 8;

const LEFT_X = 72;
const LEFT_WIDTH = 470;
const RIGHT_X = 578;
const RIGHT_WIDTH = 630;
const TOP_Y = 96;
/**
 * 一列能用的高度。
 *
 * ⚠️ **必须从画布反推，不能写死一个数再乘 UI 倍率。**
 * 写死 560 的话，UI 倍率调到 2 就变成 1120 —— 直接顶出 800 的画布。
 * 这里减掉的是：顶部偏移、面板标题栏 + 上下内边距、底部页脚余量。
 */
function availableColumnHeight(canvasHeight: number, scale: number): number {
  const overhead = uiPad(TOP_Y + 24 + 32 + 24, scale);
  return Math.max(uiPad(160, scale), canvasHeight - overhead);
}

/**
 * **把房间配置导出成文件。**
 *
 * 只做 DOM 那一小段（Blob + 一个不可见的 a）；序列化在 roomPreset.ts，
 * 那里能在 node 里断言。
 */
function downloadRoomPreset(patch: ConfigPatch | null): void {
  if (patch === null) return;
  const blob = new Blob([serializeRoomPreset(patch)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = 'ivory-roulette-room.json';
  anchor.rel = 'noopener';
  anchor.click();
  URL.revokeObjectURL(url);
}

export function createRoomScreen(ctx: ScreenContext): Screen {
  const container = new Container();
  container.label = 'screen-room';
  let column = createColumn(1);
  /** 订阅房间变化：游戏规则一改就重画这一屏 */
  let unsubscribe: (() => void) | null = null;
  /** 让重建期间不会把订阅又叠一层 */
  let dirty = false;
  /**
   * 两个滚动区的现场引用。
   *
   * ⚠️ `rebuildNow` 是**整屏重建**（`container.removeChildren()` + `build()`），
   *    新 scrollArea 的 offset 从 0 开始。不记住旧位置的话，改一条游戏规则就把
   *    整个窗口弹回顶部，玩家得重新滚下去（用户报的）。
   */
  let leftScrollArea: ReturnType<typeof scrollArea> | null = null;
  let rulesScrollArea: ReturnType<typeof scrollArea> | null = null;

  /** 「刷新同步」按过之后按钮上的短暂提示；超时后自己清掉并重画 */
  let roomSyncNote: string | null = null;
  let roomSyncTimer: ReturnType<typeof setTimeout> | null = null;

  const build = (): void => {
    const scale = ctx.settings.get().uiScale;
    const { width, height } = ctx.bounds;
    const state = ctx.room.get();

    column = createColumn(scale);
    container.addChild(column.container);
    column.addRaw(pageChrome({ scale, width, height, header: bi('common.room'), page: 'P.03' }));

    if (state === null) {
      // 直接进这一屏（比如刷新后）而没先建/进房间：给一条回去的路，
      // 而不是白屏或者抛异常
      column.addRaw(
        notice({
          text: t('room.noRoom'),
          scale,
          width: 640,
          tone: 'warn',
        }).container,
      );
      const back = stampButton({
        label: t('room.backToMeetup'),
        scale,
        variant: 'primary',
        onPress: () => ctx.stack.push(SCREEN.meetup),
      });
      back.container.position.set(uiPad(LEFT_X, scale), uiPad(160, scale));
      column.addRaw(back.container);
      return;
    }

    const creator = isSelfCreator(state);
    const host = electGameHost(state.players);

    // ---------------- 左栏：房号 + 玩家 + 操作（**一个**滚动区） ----------------
    //
    // ⚠️ 和右栏同一个理由：内容高度由数据决定（玩家数 1–5、开局问题若干条），
    //    写死高度迟早被挤爆。整列收进滚动区，画布就永远不会被顶出去。
    const leftPanel = panel({
      scale,
      width: LEFT_WIDTH,
      // 标题不再叫"房间 / ROOM"：页眉和房号大字都已经说了这件事，
      // 三处重复会把版面读起来很吵
      title: bi('room.lobbyTitle'),
      padding: PANEL_PADDING,
    });
    leftPanel.container.position.set(uiPad(LEFT_X, scale), uiPad(TOP_Y, scale));
    column.addRaw(leftPanel.container);

    const leftWidth = LEFT_WIDTH - uiPad(PANEL_PADDING * 2 + CONTENT_INSET, scale);
    const leftArea = scrollArea({
      scale,
      // 内容 `leftWidth` 宽，遮罩左右各留 `CONTENT_INSET` —— 两边都不裁
      width: leftWidth,
      bleed: uiPad(CONTENT_INSET, scale),
      height: availableColumnHeight(height, scale),
    });
    leftPanel.body.addChild(leftArea.container);

    const left = createColumn(scale, 10);
    leftArea.content.addChild(left.container);
    leftScrollArea = leftArea;

    // 房号：长得像按钮，点一下复制**邀请链接**（点开自动进这个房间）。
    // hover / 按下 / "已复制"都在 copyCode 里。
    left.add(
      copyCode({
        scale,
        text: '#' + state.code,
        copyText: roomJoinUrl(window.location.href, state.code),
      }),
    );
    left.add(
      richLabel({ text: t('room.codeHint'), scale, size: 11, fill: INK.textDim, role: 'cjk' }),
      0,
      uiPad(6, scale),
    );

    // ---- 玩家 ----
    const playersTitle = sectionTitle({
      text: `${bi('room.players')} ${t('room.playersCount', { n: state.players.length, max: MAX_PLAYERS })}`,
      scale,
      width: leftWidth,
    });
    if (creator) {
      // ★ 列表旁的按钮：随机选一个玩家先手（和点名字二选一）
      const randomFirst = stampButton({
        label: t('room.randomFirst'),
        scale,
        variant: 'quiet',
        padding: { x: 10, y: 6 },
        onPress: () => {
          ctx.room.patch({ firstPlayer: 'random' });
          rebuildSoon();
        },
      });
      const head = new Container();
      head.addChild(playersTitle.container);
      randomFirst.container.position.set(leftWidth - randomFirst.width, 0);
      head.addChild(randomFirst.container);
      left.add({ container: head, width: leftWidth, height: playersTitle.height }, 0, uiPad(4, scale));
    } else {
      left.add(playersTitle, 0, uiPad(4, scale));
    }
    for (const player of state.players) {
      left.add(playerRow(ctx, player, host, creator, leftWidth, () => rebuildSoon()));
    }

    // ★ 创建者：加一个机器人席位（没有 peer，进对局后由房主代打）
    if (creator) {
      left.add(
        // ★ 和「＋ 测试玩家」同一个样式（quiet、按字宽），不再是通栏主按钮
        stampButton({
          label: t('room.addBot'),
          scale,
          variant: 'quiet',
          padding: { x: 12, y: 8 },
          onPress: () => {
            ctx.room.addBot();
            rebuildSoon();
          },
        }),
        0,
        uiPad(4, scale),
      );
    }

    // 创建者可以看到一个"拉一个测试玩家进来"的按钮：
    // 没有传输层就凑不出第二个人，而踢人/准备/房主徽章全都要有人才看得出对不对。
    // ⚠️ **只在 dev build 存在**（生产构建里 `import.meta.env.DEV` 是编译期 false，
    //    整块被摇掉）。联机落地后整块删掉。
    if (creator && import.meta.env.DEV) {
      const nextIndex = state.players.length;
      left.add(
        stampButton({
          label: t('room.addTestPlayer'),
          scale,
          variant: 'quiet',
          padding: { x: 12, y: 8 },
          onPress: () => {
            ctx.room.addTestPlayer(`dev-${nextIndex}`, `PLAYER${nextIndex}`, 20 + nextIndex * 37);
            rebuildSoon();
          },
        }),
        0,
        uiPad(4, scale),
      );
    }

    // ---- 操作 ----
    left.add(sectionTitle({ text: bi('room.actions'), scale, width: leftWidth }), 0, uiPad(6, scale));

    if (!creator) {
      // 非创建者不开局：这个按钮就是"准备"开关（原来的复选框删掉了）
      const self = findPlayer(state, state.selfId);
      const ready = self?.ready ?? false;
      left.add(
        stampButton({
          label: ready ? t('room.unready') : t('room.ready'),
          scale,
          variant: ready ? 'quiet' : 'primary',
          width: leftWidth,
          onPress: () => {
            ctx.room.setReady(!ready);
            rebuildSoon();
          },
        }),
      );
    }

    // ★ 把"开局后谁当房主"直接写在脸上。
    //   这条规则（网络最快者，和创建者无关）平时看不见，只有摆出来才验得了
    left.add(
      infoRow({
        label: t('common.host'),
        value: host === null ? '—' : t('room.hostValue', { name: findPlayer(state, host)?.nickname ?? host }),
        scale,
        width: leftWidth,
        valueFill: INK.accent,
      }),
    );

    // 还没凑齐人 / 还有人没准备时，摆一条**断点信号条**（`docs/ui-design.md` §6）。
    // 它就是打样页那个 `.uibar`：等一个响应窗口和等一屋子人准备，
    // 在"有人欠一个表态"这件事上是同一个形状
    const waiting =
      state.players.length < 2 || state.players.some((player) => !player.isCreator && !player.ready);
    if (waiting) {
      left.add(
        signalBar({
          kind: 'break',
          scale,
          width: leftWidth,
          label: state.players.length < 2 ? t('room.waitingPlayers') : t('room.waitingReady'),
          code: 'WAIT',
        }),
      );
    }

    const check = ctx.room.canStart();
    // 开局 = 进对局屏。热座直接开；联机那一版由创建者广播一次
    // `lobby/start`，所有人（含自己）各自把名单与游戏规则交给 `S3Session`
    // （`src/host/`，见 `docs/netcode-s3.md`）
    const net = ctx.online?.() ?? null;
    // 只有创建者能开局（联机对局不由非房主玩家开始）
    if (creator) {
      const startButton = stampButton({
        label: check.ok ? (net === null ? t('room.startHotseat') : t('room.start')) : t('room.cannotStart'),
        scale,
        variant: 'primary',
        width: leftWidth,
        onPress: () => {
          if (net === null) {
            // 本机热座（离线房间）：走 `/hotseat`，和联机对局分开
            ctx.stack.push(SCREEN.hotseat);
            return;
          }
          // 联机：创建者宣布开局，**所有人**（含自己）各自开一局。
          net.startGame(Date.now() >>> 0);
          ctx.stack.push(SCREEN.game);
        },
      });
      startButton.setEnabled(check.ok);
      left.add(startButton);
    }

    // 把"为什么还不能开"一条条摆出来 —— 创建者看到的应该是自己配置的问题，
    // 而不是一个灰按钮
    if (creator && !check.ok) {
      for (const problem of check.problems) {
        left.add(notice({ text: problem, scale, width: leftWidth, tone: 'warn' }));
      }
    }

    // 操作失败的原因（比如非创建者试图改游戏规则）
    const error = ctx.room.lastError();
    if (error !== null) {
      left.add(notice({ text: error, scale, width: leftWidth, tone: 'danger' }));
      ctx.room.clearError();
    }

    // 「设置」在等开局的时候最有用（改昵称、调音量）。
    // ⚠️ 它开的是**叠加层**而不是新屏幕 —— 房间状态原样留在底下，
    //    关掉设置就回到这里。评审要的"设置能盖在任意界面之上"在房间页就靠它兑现
    // ★ 手动刷新（只联机有）：要求所有人立刻重播 presence，创建者补发配置。
    //   房间页卡在旧名册/旧配置时按一下，不必退出重进。
    if (net !== null) {
      left.add(
        stampButton({
          label: roomSyncNote ?? t('room.sync'),
          scale,
          variant: 'quiet',
          width: leftWidth,
          onPress: () => {
            net.refresh();
            roomSyncNote = t('room.syncRequested');
            if (roomSyncTimer !== null) clearTimeout(roomSyncTimer);
            roomSyncTimer = setTimeout(() => {
              roomSyncTimer = null;
              roomSyncNote = null;
              rebuildSoon();
            }, 2500);
            rebuildSoon();
          },
        }),
      );
    }

    left.add(
      stampButton({
        label: t('common.settings'),
        scale,
        variant: 'secondary',
        width: leftWidth,
        onPress: () => ctx.stack.openOverlay(OVERLAY.settings),
      }),
    );

    left.add(
      stampButton({
        label: t('room.leave'),
        scale,
        variant: 'danger',
        width: leftWidth,
        onPress: () => {
          ctx.room.leaveRoom();
          ctx.stack.push(SCREEN.title);
        },
      }),
    );

    leftArea.setContentHeight(Math.round(left.y));
    leftPanel.fitContent(leftArea.height);

    // ---------------- 右栏：游戏规则 + 牌堆（**一个**滚动区） ----------------
    //
    // ⚠️ 早先是两个独立面板（游戏规则一个、牌堆一个），结果是右边的内容
    //    比 800px 的画布高得多，两块面板必然互相盖住。
    //    现在整列收进**一个**滚动区：面板高度固定，内容在里面滚。
    //    菜单里凡是"内容长度由数据决定"的地方都该这么做 ——
    //    靠算高度去挤，迟早会被多出来的一行挤爆。
    const rulesPanel = panel({
      scale,
      width: RIGHT_WIDTH,
      title: creator ? bi('common.rulesAndDeck') : bi('common.rulesAndDeck') + t('room.readonly'),
      padding: PANEL_PADDING,
    });
    rulesPanel.container.position.set(uiPad(RIGHT_X, scale), uiPad(TOP_Y, scale));
    column.addRaw(rulesPanel.container);

    const rulesWidth = RIGHT_WIDTH - uiPad(PANEL_PADDING * 2 + CONTENT_INSET, scale);
    const area = scrollArea({
      scale,
      width: rulesWidth,
      bleed: uiPad(CONTENT_INSET, scale),
      height: availableColumnHeight(height, scale),
    });
    rulesPanel.body.addChild(area.container);

    const rulesInner = createColumn(scale, 10);
    area.content.addChild(rulesInner.container);
    rulesScrollArea = area;

    buildRules(ctx, rulesInner, rulesWidth, creator, () => rebuildSoon());

    // ---- 牌堆 ----
    rulesInner.add(
      sectionTitle({ text: bi('room.deckTitle'), scale, width: rulesWidth }),
      0,
      uiPad(12, scale),
    );
    rulesInner.add(
      infoRow({
        label: t('room.deckTotal'),
        value: String(deckTotal(state)),
        scale,
        width: rulesWidth,
        valueFill: INK.accent,
      }),
    );

    const deckActions = new Container();
    const resetDeckButton = stampButton({
      label: t('common.resetDefaults'),
      scale,
      variant: 'secondary',
      onPress: () => {
        ctx.room.resetDeck();
        rebuildSoon();
      },
    });
    resetDeckButton.setEnabled(creator);
    // ★ 导出当前配置为文件（只含和官方默认不同的字段）。
    //   导入走"把文件拖到页面任意位置"，见 onDrop。
    const exportPreset = stampButton({
      label: t('room.exportConfig'),
      scale,
      variant: 'quiet',
      onPress: () => downloadRoomPreset(ctx.room.presetDiff()),
    });
    exportPreset.setEnabled(creator);
    exportPreset.container.position.set(resetDeckButton.width + uiPad(STAMP_GAP, scale), 0);
    deckActions.addChild(resetDeckButton.container, exportPreset.container);
    rulesInner.add({ container: deckActions, width: rulesWidth, height: resetDeckButton.height });

    rulesInner.add(
      notice({
        text:
          state.importedSave === null
            ? t('room.importHint')
            : t('room.imported', { name: state.importedSave }),
        scale,
        width: rulesWidth,
      }),
    );

    const cards = [...ctx.registry.cards].sort((a, b) => a.local.localeCompare(b.local));
    for (const card of cards) {
      const count = state.config.deck[card.id] ?? 0;
      rulesInner.add(
        stepper({
          label: `${cardName(card)}  ${card.local}`,
          scale,
          value: count,
          min: 0,
          max: MAX_COPIES_PER_TITLE,
          width: rulesWidth,
          enabled: creator,
          onChange: (value) => {
            ctx.room.setDeckCount(card.id, value);
            rebuildSoon();
          },
        }),
      );
    }

    if (!creator) {
      rulesInner.add(
        notice({
          text: t('room.creatorOnly'),
          scale,
          width: rulesWidth,
        }),
      );
    }

    area.setContentHeight(Math.round(rulesInner.y));
    rulesPanel.fitContent(area.height);
  };

  /** 房间状态变了就重画。放在微任务里，免得同一个事件里重建多次 */
  const rebuildSoon = (): void => {
    if (dirty) return;
    dirty = true;
    queueMicrotask(() => {
      dirty = false;
      rebuildNow();
    });
  };

  const rebuildNow = (): void => {
    // ★ 记住滚动位置，重建后放回去 —— 改游戏规则不该把窗口弹回顶部
    const leftY = leftScrollArea?.scrollY ?? 0;
    const rulesY = rulesScrollArea?.scrollY ?? 0;
    column.dispose();
    container.removeChildren();
    build();
    leftScrollArea?.scrollTo(leftY);
    rulesScrollArea?.scrollTo(rulesY);
  };

  /** 房间配置导入：把文件拖到页面任意位置。只在住这一屏时挂在 window 上 */
  const onDragOver = (event: DragEvent): void => {
    // 不 preventDefault 的话浏览器会直接打开这个文件
    event.preventDefault();
  };
  const onDrop = (event: DragEvent): void => {
    event.preventDefault();
    const file = event.dataTransfer?.files?.[0];
    if (file === undefined) return;
    void file.text().then((text) => {
      const parsed = parseRoomPreset(text);
      if (!parsed.ok) {
        ctx.room.fail(parsed.reason);
        return;
      }
      ctx.room.patch(parsed.patch);
      ctx.room.setImportedSave(file.name);
    });
  };

  build();

  return {
    container,
    onEnter() {
      unsubscribe = ctx.room.subscribe(() => rebuildSoon());
      // ★ 恢复上次离开时保存的房间配置（只有创建者能改；patch 内部会挡）
      if (ctx.room.isCreator()) {
        const preset = ctx.room.loadPreset();
        if (preset !== null) ctx.room.patch(preset);
      }
      window.addEventListener('dragover', onDragOver);
      window.addEventListener('drop', onDrop);
    },
    onExit() {
      // ★ 每次离开房间页存一份（只存和官方默认不同的字段）
      ctx.room.savePreset();
      unsubscribe?.();
      unsubscribe = null;
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('drop', onDrop);
    },
    rebuild() {
      rebuildNow();
    },
    onKey(event) {
      if (event.key !== 'Escape') return false;
      ctx.room.leaveRoom();
      ctx.stack.push(SCREEN.title);
      return true;
    },
    destroy() {
      if (roomSyncTimer !== null) {
        clearTimeout(roomSyncTimer);
        roomSyncTimer = null;
      }
      unsubscribe?.();
      unsubscribe = null;
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('drop', onDrop);
      column.dispose();
      container.destroy({ children: true });
    },
  };
}

// ============================================================
// 一个玩家一行
// ============================================================

function playerRow(
  ctx: ScreenContext,
  player: LobbyPlayer,
  hostId: string | null,
  creator: boolean,
  width: number,
  refresh: () => void,
): { container: Container; width: number; height: number } {
  const scale = ctx.settings.get().uiScale;
  const rowHeight = uiPad(30, scale);
  const container = new Container();
  const state = ctx.room.get();
  const self = player.id === state?.selfId;

  // 就绪方块：开着是实心，关着是空心。不用对勾（那要图标字体，见视觉反模式）
  const mark = uiPad(12, scale);
  const ready = player.isCreator || player.ready;
  const graphics = new Graphics();
  graphics
    .rect(0, Math.round((rowHeight - mark) / 2), mark, mark)
    .fill({ color: ready ? INK.ok : INK.panel })
    .stroke({ color: ready ? INK.ok : INK.edge, width: 1, alignment: 0 });
  container.addChild(graphics);

  const firstPlayer = state?.config.firstPlayer ?? 'random';
  const isFirst = firstPlayer === player.id;
  // ★ 先手不再靠名字变色表示，改成名字后面的 badge（和房主/主机一致）。
  //   名字只保留"这是我"的高亮。
  const nameFill = self ? INK.accent : INK.text;
  const name = richLabel({
    text: player.nickname,
    scale,
    size: 13,
    fill: nameFill,
    role: 'mono',
    bold: self,
  });
  const nameBaseY = Math.round((rowHeight - name.height) / 2);
  name.position.set(mark + uiPad(10, scale), nameBaseY);
  container.addChild(name);
  // ★ 创建者点玩家名字 = 设他为**起始玩家**（指针事件走组件库的 createSurface）
  if (creator) {
    const paintName = (control: ControlState, motion: ControlMotion): void => {
      name.position.y = nameBaseY + motion.lift;
      name.style.fill = control === 'hover' ? INK.accent : nameFill;
    };
    createSurface({
      container: name,
      width: name.width,
      height: name.height,
      cursor: 'pointer',
      onPress: () => {
        ctx.room.patch({ firstPlayer: player.id as PlayerId });
        refresh();
      },
      onStateChange: paintName,
      onFrame: paintName,
    });
  }

  // 身份分开挂章 —— 见文件头。「房主」是权限，「主机」是网络最快者，
  // 「先手」是房主点名的那个人。章走组件库的 `stamp`
  // （小、被框住、不出血 —— 打样页那条"标记"的纪律）
  let badgeX = mark + uiPad(16, scale) + name.width;
  const badges: readonly { text: string; tone: 'knockout' | 'accent' | 'ok' }[] = [
    ...(player.isCreator ? [{ text: t('room.badge.creator'), tone: 'knockout' as const }] : []),
    ...(player.id === hostId ? [{ text: t('common.host'), tone: 'accent' as const }] : []),
    ...(isFirst ? [{ text: t('room.badge.first'), tone: 'ok' as const }] : []),
  ];
  for (const spec of badges) {
    const badge = stamp({ text: spec.text, scale, tone: spec.tone });
    badge.container.position.set(badgeX, Math.round((rowHeight - badge.height) / 2));
    container.addChild(badge.container);
    badgeX += badge.width + uiPad(6, scale);
  }

  // 延迟。它决定谁当对局房主，所以这一列是有意义的，不是装饰
  const kickWidth = creator && !self ? uiPad(56, scale) : 0;
  const ping = richLabel({
    text: player.pingMs === null ? '—' : `${Math.round(player.pingMs)}ms`,
    scale,
    size: 11,
    fill: INK.textDim,
    role: 'mono',
    align: 'right',
  });
  ping.anchor.set(1, 0);
  ping.position.set(width - kickWidth - uiPad(8, scale), Math.round((rowHeight - ping.height) / 2));
  container.addChild(ping);

  // 踢人（只有创建者看得见、点得动）
  if (kickWidth > 0) {
    const kick = stampButton({
      label: t('room.kick'),
      scale,
      variant: 'danger',
      width: kickWidth,
      height: uiPad(22, scale),
      size: 11,
      onPress: () => {
        ctx.room.kick(player.id);
        refresh();
      },
    });
    kick.container.position.set(width - kickWidth, Math.round((rowHeight - kick.height) / 2));
    container.addChild(kick.container);
  }

  return { container, width, height: rowHeight };
}

// ============================================================
// 游戏规则编辑器
// ============================================================

function buildRules(
  ctx: ScreenContext,
  inner: ReturnType<typeof createColumn>,
  width: number,
  creator: boolean,
  refresh: () => void,
): void {
  const state = ctx.room.get();
  if (state === null) return;
  const scale = ctx.settings.get().uiScale;
  const config = state.config;

  const patch = (value: Parameters<typeof ctx.room.patch>[0]): void => {
    ctx.room.patch(value);
    refresh();
  };

  // ---- 内容包（汇总所有出现过的包；房主只能在交集里启用） ----
  inner.add(sectionTitle({ text: bi('room.packsTitle'), scale, width }));
  for (const status of packStatuses(state)) {
    const pack = ctx.registry.packs.find((candidate) => candidate.id === status.id);
    inner.add(
      toggle({
        label: pack?.name ?? String(status.id),
        scale,
        value: status.enabled,
        width,
        // ★ 只能启用"全员都有且指纹一致"的包；必需包不可关
        enabled: creator && status.universal && pack?.required !== true,
        note: status.reason ?? t('room.everyoneHas'),
        onChange: (value) => {
          const next = value
            ? ctx.registry.packs
                .filter(
                  (candidate) =>
                    candidate.id === status.id || config.enabledPacks.includes(candidate.id),
                )
                .map((candidate) => candidate.id)
            : config.enabledPacks.filter((id) => id !== status.id);
          patch({ enabledPacks: next });
        },
      }),
    );
  }

  // ---- 数据驱动的设置项 ----
  // 有哪些设置、什么类型、怎么生效 —— 全在 houseRules.ts 那张表里。
  // 这里只负责渲染；加一条规则改表，不改编排代码。
  let section: string | null = null;
  for (const rule of houseRules.all()) {
    if (rule.section !== section) {
      section = rule.section;
      inner.add(sectionTitle({ text: bi(rule.section), scale, width }));
    }
    renderHouseRule(ctx, inner, width, scale, config, rule, creator, patch);
  }
}

/**
 * 把一条游戏规则描述渲染成一个（或一对）控件。
 *
 * 只认表的 control.kind —— 加一种新类型才需要动这个 switch。
 */
function renderHouseRule(
  ctx: ScreenContext,
  inner: ReturnType<typeof createColumn>,
  width: number,
  scale: number,
  config: RoomConfig,
  rule: HouseRule,
  creator: boolean,
  patch: (value: ConfigPatch) => void,
): void {
  const enabled = creator && (rule.enabled?.(config) ?? true);
  // ⚠️ section/label/note/toggleLabel 存的是 i18n 键，渲染时才 t() —— 登记在
  //    导入期，若那时就 t() 会冻在导入时的语言（见 houseRules.ts）。
  const label = t(rule.label);
  const withNote = rule.note === undefined ? {} : { note: t(rule.note) };
  const control = rule.control;

  if (control.kind === 'toggle') {
    inner.add(
      toggle({
        label,
        scale,
        value: Boolean(rule.value(config)),
        width,
        enabled,
        ...withNote,
        onChange: (value) => patch(rule.patch(value)),
      }),
    );
    return;
  }

  if (control.kind === 'number') {
    inner.add(
      stepper({
        label,
        scale,
        value: Number(rule.value(config)),
        min: control.min,
        max: control.max,
        ...(control.step === undefined ? {} : { step: control.step }),
        width,
        enabled,
        onChange: (value) => patch(rule.patch(value)),
      }),
    );
    return;
  }

  if (control.kind === 'auto-number') {
    const raw = rule.value(config) as number | null;
    const auto = raw === null;
    inner.add(
      toggle({
        label: t('room.autoByPlayerCount', { label }),
        scale,
        value: auto,
        width,
        enabled,
        ...withNote,
        onChange: (value) => patch(rule.patch(value ? null : control.manualFallback(config))),
      }),
    );
    inner.add(
      stepper({
        label,
        scale,
        value: raw ?? control.autoValue(config),
        min: control.min,
        max: control.max,
        ...(control.step === undefined ? {} : { step: control.step }),
        width,
        enabled: enabled && !auto,
        onChange: (value) => patch(rule.patch(value)),
      }),
    );
    return;
  }

  // toggle-number：开关表示不限，关着时用滑块
  const value = Number(rule.value(config));
  const unlimited = value === control.offValue;
  inner.add(
    toggle({
      label: t(control.toggleLabel),
      scale,
      value: unlimited,
      width,
      enabled,
      ...withNote,
      onChange: (on) => patch(rule.patch(on ? control.offValue : control.onValue)),
    }),
  );
  const bar = slider({
    label,
    scale,
    min: control.min,
    max: control.max,
    step: control.step,
    value: unlimited ? control.onValue : value,
    width,
    input: ctx.input,
    enabled: enabled && !unlimited,
    ...(control.format === undefined ? {} : { format: control.format }),
    onCommit: (next) => patch(rule.patch(next)),
  });
  inner.add(bar);
  inner.track(() => bar.destroy());
}
